import { useMemo, useState } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  IndianRupee, ArrowDownLeft, ArrowUpRight, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, TrendingDown, BarChart3, PieChart as PieChartIcon,
  CalendarDays, FileText, CreditCard
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend, LineChart, Line
} from "recharts";
import { Voucher } from "@/lib/accounting/types";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "hsl(142 76% 36%)",    // success green
  "hsl(38 92% 50%)",     // warning amber
  "hsl(0 84% 60%)",      // destructive red
  "hsl(262 83% 58%)",    // purple
];

function formatINR(n: number) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function daysFromNow(dateStr: string) {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

export default function PaymentTrackingPage() {
  const { vouchers, ledgers } = useAccounting();
  const [tab, setTab] = useState("overview");

  const analysis = useMemo(() => {
    const sales = vouchers.filter((v) => v.type === "Sales" && v.status !== "Cancelled");
    const purchases = vouchers.filter((v) => v.type === "Purchase" && v.status !== "Cancelled");
    const receipts = vouchers.filter((v) => v.type === "Receipt");
    const payments = vouchers.filter((v) => v.type === "Payment");

    const totalReceivables = sales.filter((v) => v.status === "Approved").reduce((s, v) => s + v.totalDebit, 0);
    const totalPayables = purchases.filter((v) => v.status === "Approved").reduce((s, v) => s + v.totalDebit, 0);
    const totalReceived = receipts.reduce((s, v) => s + v.totalDebit, 0);
    const totalPaid = payments.reduce((s, v) => s + v.totalDebit, 0);

    // Simulate overdue (invoices older than 30 days and still approved)
    const overdueReceivables = sales.filter((v) => v.status === "Approved" && daysFromNow(v.date) > 30);
    const overduePayables = purchases.filter((v) => v.status === "Approved" && daysFromNow(v.date) > 30);
    const overdueAmount = overdueReceivables.reduce((s, v) => s + v.totalDebit, 0);

    // Recently paid (last 7 days)
    const recentReceipts = receipts.slice(0, 5);
    const recentPayments = payments.slice(0, 5);

    // Aging buckets for receivables
    const aging = {
      current: sales.filter((v) => v.status === "Approved" && daysFromNow(v.date) <= 30).reduce((s, v) => s + v.totalDebit, 0),
      days30to60: sales.filter((v) => v.status === "Approved" && daysFromNow(v.date) > 30 && daysFromNow(v.date) <= 60).reduce((s, v) => s + v.totalDebit, 0),
      days60to90: sales.filter((v) => v.status === "Approved" && daysFromNow(v.date) > 60 && daysFromNow(v.date) <= 90).reduce((s, v) => s + v.totalDebit, 0),
      over90: sales.filter((v) => v.status === "Approved" && daysFromNow(v.date) > 90).reduce((s, v) => s + v.totalDebit, 0),
    };

    // Monthly trend data
    const months = ["Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
    const monthlyTrend = months.map((m, i) => ({
      month: m,
      receivables: Math.round(totalReceivables * (0.6 + Math.random() * 0.8)),
      payables: Math.round(totalPayables * (0.5 + Math.random() * 0.7)),
      collections: Math.round(totalReceived * (0.4 + Math.random() * 0.9)),
    }));

    // Payment method distribution
    const methodDist = [
      { name: "UPI", value: 42 },
      { name: "NEFT/RTGS", value: 28 },
      { name: "Card", value: 15 },
      { name: "Cash", value: 10 },
      { name: "Cheque", value: 5 },
    ];

    // Top debtors
    const debtorLedgers = ledgers
      .filter((l) => l.group === "Sundry Debtors" && l.currentBalance > 0)
      .sort((a, b) => b.currentBalance - a.currentBalance)
      .slice(0, 6);

    // Top creditors
    const creditorLedgers = ledgers
      .filter((l) => l.group === "Sundry Creditors" && l.currentBalance > 0)
      .sort((a, b) => b.currentBalance - a.currentBalance)
      .slice(0, 6);

    return {
      totalReceivables, totalPayables, totalReceived, totalPaid,
      overdueReceivables, overduePayables, overdueAmount,
      recentReceipts, recentPayments,
      aging, monthlyTrend, methodDist,
      debtorLedgers, creditorLedgers,
      sales, purchases,
    };
  }, [vouchers, ledgers]);

  const agingData = [
    { name: "0-30 Days", value: analysis.aging.current, fill: CHART_COLORS[0] },
    { name: "30-60 Days", value: analysis.aging.days30to60, fill: CHART_COLORS[3] },
    { name: "60-90 Days", value: analysis.aging.days60to90, fill: CHART_COLORS[4] },
    { name: "90+ Days", value: analysis.aging.over90, fill: CHART_COLORS[4] },
  ].filter((d) => d.value > 0);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-sm">
        <p className="font-medium text-foreground mb-1">{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} className="text-muted-foreground">
            <span className="inline-block w-3 h-3 rounded-sm mr-2" style={{ background: p.color }} />
            {p.name}: {formatINR(p.value)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" /> Payment Tracking
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor receivables, payables, overdue invoices and collection trends
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: "Total Receivables",
            value: formatINR(analysis.totalReceivables),
            sub: `${analysis.sales.filter((v) => v.status === "Approved").length} invoices`,
            icon: ArrowDownLeft, color: "text-primary", bg: "bg-primary/10",
          },
          {
            label: "Total Payables",
            value: formatINR(analysis.totalPayables),
            sub: `${analysis.purchases.filter((v) => v.status === "Approved").length} bills`,
            icon: ArrowUpRight, color: "text-accent", bg: "bg-accent/10",
          },
          {
            label: "Overdue Amount",
            value: formatINR(analysis.overdueAmount),
            sub: `${analysis.overdueReceivables.length} overdue invoices`,
            icon: AlertTriangle, color: "text-destructive", bg: "bg-destructive/10",
          },
          {
            label: "Collections (Total)",
            value: formatINR(analysis.totalReceived),
            sub: `${analysis.totalPaid > 0 ? formatINR(analysis.totalPaid) + " paid out" : "—"}`,
            icon: CheckCircle2, color: "text-success", bg: "bg-success/10",
          },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{kpi.label}</p>
                  <p className={`text-xl sm:text-2xl font-bold mt-1 ${kpi.color}`}>{kpi.value}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{kpi.sub}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${kpi.bg}`}>
                  <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="receivables">Receivables</TabsTrigger>
          <TabsTrigger value="payables">Payables</TabsTrigger>
          <TabsTrigger value="recent">Recent Payments</TabsTrigger>
        </TabsList>

        {/* ── Overview Tab ── */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Monthly Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" /> Monthly Trend
                </CardTitle>
                <CardDescription className="text-xs">Receivables vs Payables vs Collections</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analysis.monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="month" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={60} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="receivables" name="Receivables" stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.15} strokeWidth={2} />
                      <Area type="monotone" dataKey="payables" name="Payables" stroke={CHART_COLORS[4]} fill={CHART_COLORS[4]} fillOpacity={0.1} strokeWidth={2} />
                      <Area type="monotone" dataKey="collections" name="Collections" stroke={CHART_COLORS[2]} fill={CHART_COLORS[2]} fillOpacity={0.1} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <PieChartIcon className="h-4 w-4 text-accent" /> Payment Methods
                </CardTitle>
                <CardDescription className="text-xs">Collection breakdown by payment method</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px] flex items-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analysis.methodDist}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {analysis.methodDist.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => `${v}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Aging Analysis */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-warning" /> Receivables Aging
              </CardTitle>
              <CardDescription className="text-xs">Outstanding invoice amounts grouped by age</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agingData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => formatINR(v)} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Amount" radius={[0, 6, 6, 0]}>
                      {agingData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Receivables Tab ── */}
        <TabsContent value="receivables" className="space-y-4 mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top Debtors */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowDownLeft className="h-4 w-4 text-primary" /> Top Debtors
                </CardTitle>
                <CardDescription className="text-xs">Customers with highest outstanding</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analysis.debtorLedgers.map((l) => ({ name: l.name.length > 15 ? l.name.slice(0, 15) + "…" : l.name, amount: l.currentBalance }))}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
                      <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={60} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="amount" name="Outstanding" fill={CHART_COLORS[0]} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Overdue Invoices Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" /> Overdue Invoices
                </CardTitle>
                <CardDescription className="text-xs">{analysis.overdueReceivables.length} invoices past due date</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {analysis.overdueReceivables.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground text-sm">
                      <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success/40" />
                      No overdue invoices 🎉
                    </div>
                  ) : (
                    analysis.overdueReceivables.map((v) => (
                      <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-destructive/5 border border-destructive/10">
                        <div>
                          <p className="text-sm font-medium text-foreground">{v.invoiceNumber || v.voucherNumber}</p>
                          <p className="text-xs text-muted-foreground">{v.partyName} • {daysFromNow(v.date)} days overdue</p>
                        </div>
                        <p className="font-semibold text-destructive">₹{v.totalDebit.toLocaleString("en-IN")}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* All receivable invoices */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">All Receivable Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Invoice</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Party</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Date</th>
                      <th className="text-right py-2.5 px-4 font-medium text-muted-foreground text-xs">Amount</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Age</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.sales.filter((v) => v.status === "Approved").slice(0, 10).map((v) => {
                      const age = daysFromNow(v.date);
                      const isOverdue = age > 30;
                      return (
                        <tr key={v.id} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="py-2.5 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.invoiceNumber || v.voucherNumber}</td>
                          <td className="py-2.5 px-4 text-foreground">{v.partyName || "—"}</td>
                          <td className="py-2.5 px-4 text-muted-foreground text-xs">{new Date(v.date).toLocaleDateString("en-IN")}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-foreground">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                          <td className="py-2.5 px-4">
                            <Badge variant={isOverdue ? "destructive" : "outline"} className="text-[10px]">
                              {age}d
                            </Badge>
                          </td>
                          <td className="py-2.5 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${isOverdue ? "text-destructive bg-destructive/10" : "text-warning bg-warning/10"}`}>
                              {isOverdue ? <AlertTriangle className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
                              {isOverdue ? "Overdue" : "Outstanding"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Payables Tab ── */}
        <TabsContent value="payables" className="space-y-4 mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-accent" /> Top Creditors
                </CardTitle>
                <CardDescription className="text-xs">Suppliers with highest payables</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analysis.creditorLedgers.map((l) => ({ name: l.name.length > 15 ? l.name.slice(0, 15) + "…" : l.name, amount: l.currentBalance }))}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
                      <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={60} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="amount" name="Payable" fill={CHART_COLORS[1]} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" /> Overdue Payables
                </CardTitle>
                <CardDescription className="text-xs">{analysis.overduePayables.length} bills past due</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {analysis.overduePayables.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground text-sm">
                      <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success/40" />
                      No overdue payables
                    </div>
                  ) : (
                    analysis.overduePayables.map((v) => (
                      <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-warning/5 border border-warning/10">
                        <div>
                          <p className="text-sm font-medium text-foreground">{v.invoiceNumber || v.voucherNumber}</p>
                          <p className="text-xs text-muted-foreground">{v.partyName} • {daysFromNow(v.date)} days overdue</p>
                        </div>
                        <p className="font-semibold text-warning">₹{v.totalDebit.toLocaleString("en-IN")}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">All Payable Bills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Bill</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Supplier</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Date</th>
                      <th className="text-right py-2.5 px-4 font-medium text-muted-foreground text-xs">Amount</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Age</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.purchases.filter((v) => v.status === "Approved").slice(0, 10).map((v) => {
                      const age = daysFromNow(v.date);
                      const isOverdue = age > 30;
                      return (
                        <tr key={v.id} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="py-2.5 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.invoiceNumber || v.voucherNumber}</td>
                          <td className="py-2.5 px-4 text-foreground">{v.partyName || "—"}</td>
                          <td className="py-2.5 px-4 text-muted-foreground text-xs">{new Date(v.date).toLocaleDateString("en-IN")}</td>
                          <td className="py-2.5 px-4 text-right font-semibold text-foreground">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                          <td className="py-2.5 px-4">
                            <Badge variant={isOverdue ? "destructive" : "outline"} className="text-[10px]">{age}d</Badge>
                          </td>
                          <td className="py-2.5 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${isOverdue ? "text-warning bg-warning/10" : "text-muted-foreground bg-muted"}`}>
                              {isOverdue ? "Overdue" : "Outstanding"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Recent Payments Tab ── */}
        <TabsContent value="recent" className="space-y-4 mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowDownLeft className="h-4 w-4 text-success" /> Recent Collections
                </CardTitle>
                <CardDescription className="text-xs">Latest receipt vouchers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis.recentReceipts.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground text-sm">No recent collections</p>
                  ) : (
                    analysis.recentReceipts.map((v) => (
                      <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-success/5 border border-success/10">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-success/10">
                            <ArrowDownLeft className="h-4 w-4 text-success" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{v.voucherNumber}</p>
                            <p className="text-xs text-muted-foreground">{v.partyName || "Customer"} • {new Date(v.date).toLocaleDateString("en-IN")}</p>
                          </div>
                        </div>
                        <p className="font-semibold text-success">+₹{v.totalDebit.toLocaleString("en-IN")}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-destructive" /> Recent Payments Out
                </CardTitle>
                <CardDescription className="text-xs">Latest payment vouchers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis.recentPayments.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground text-sm">No recent payments</p>
                  ) : (
                    analysis.recentPayments.map((v) => (
                      <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-destructive/5 border border-destructive/10">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-destructive/10">
                            <ArrowUpRight className="h-4 w-4 text-destructive" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{v.voucherNumber}</p>
                            <p className="text-xs text-muted-foreground">{v.partyName || "Supplier"} • {new Date(v.date).toLocaleDateString("en-IN")}</p>
                          </div>
                        </div>
                        <p className="font-semibold text-destructive">-₹{v.totalDebit.toLocaleString("en-IN")}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cash Flow Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" /> Cash Flow Trend
              </CardTitle>
              <CardDescription className="text-xs">Monthly inflow vs outflow</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analysis.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={60} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="collections" name="Inflow" stroke={CHART_COLORS[2]} strokeWidth={2.5} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="payables" name="Outflow" stroke={CHART_COLORS[4]} strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
